import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ExtractionQAFileData, ExtractionQAGet, ExtractedByType } from '../../../../core/interfaces/admin-interfaces';
import { AdminService } from '../../../../core/api/admin.service';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { CustomDropdownComponent } from '../../../../shared/components/custom-dropdown/custom-dropdown.component';

interface CompareOption {
  label: string;
  from: ExtractedByType;
  to: ExtractedByType;
}

@Component({
  selector: 'app-qa-ai-tools-panel',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, CustomDropdownComponent],
  templateUrl: './qa-ai-tools-panel.component.html',
  styleUrl: './qa-ai-tools-panel.component.css'
})
export class QaAiToolsPanelComponent implements OnChanges {
  constructor(
    private translocoService: TranslocoService,
    private adminService: AdminService
  ) { }

  @Input() qaData: ExtractionQAFileData | null = null;
  @Input() toast!: ToastComponent;
  @Input() erikRecord: ExtractionQAGet | null = null;
  @Input() neoxRecord: ExtractionQAGet | null = null;
  @Input() updatedRecord: ExtractionQAGet | null = null;
  @Input() filteredComparisons: { field: string; erik: any; neox: any; updated: any; erikMatch: boolean; neoxMatch: boolean }[] = [];
  @Input() invoicePeriodType: string = 'mensile';

  // Panel state
  aiPanelOpen: boolean = false;
  allCompareOptions: CompareOption[] = [
    { label: 'V1 → V2', from: ExtractedByType.ERIK, to: ExtractedByType.NEOX },
    { label: 'V1 → Updated', from: ExtractedByType.ERIK, to: ExtractedByType.UPDATED },
    { label: 'V2 → Updated', from: ExtractedByType.NEOX, to: ExtractedByType.UPDATED },
  ];
  compareOptions: CompareOption[] = this.allCompareOptions;
  compareDropdownOptions: string[] = this.compareOptions.map(o => o.label);
  selectedCompareIndex: number = 0;

  // AI Summary
  aiSummary: string | null = null;
  aiHighlights: string[] = [];
  aiRecommendations: string[] = [];
  aiSummaryLoading: boolean = false;

  // Prompt
  generatedPrompt: string | null = null;

  get selectedCompare(): CompareOption {
    return this.compareOptions[this.selectedCompareIndex];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoicePeriodType']) {
      if (this.invoicePeriodType === 'bimestrale') {
        this.compareOptions = this.allCompareOptions.filter(o => o.from !== ExtractedByType.ERIK);
      } else {
        this.compareOptions = this.allCompareOptions;
      }
      this.compareDropdownOptions = this.compareOptions.map(o => o.label);
      this.selectedCompareIndex = 0;
    }
  }

  toggleAiPanel(): void {
    this.aiPanelOpen = !this.aiPanelOpen;
  }

  generateAiSummary(): void {
    if (!this.qaData?.stats?.file_id || this.aiSummaryLoading) return;

    this.aiSummaryLoading = true;
    this.aiSummary = null;
    this.aiHighlights = [];
    this.aiRecommendations = [];

    this.adminService.getQAAISummary({
      file_id: this.qaData.stats.file_id,
      compare_from: this.selectedCompare.from,
      compare_to: this.selectedCompare.to,
      invoice_period_type: this.invoicePeriodType,
    }).subscribe({
      next: (res) => {
        this.aiSummary = res.summary;
        this.aiHighlights = res.highlights || [];
        this.aiRecommendations = res.recommendations || [];
        this.aiSummaryLoading = false;
      },
      error: () => {
        this.toast.showToast('shared.error', 'admin.qa.ai_summary_error', 'error');
        this.aiSummaryLoading = false;
      }
    });
  }

  generateAiPrompt(): void {
    const fromRecord = this.getRecordByType(this.selectedCompare.from);
    const toRecord = this.getRecordByType(this.selectedCompare.to);

    if (!fromRecord || !toRecord) {
      this.toast.showToast('shared.error', 'admin.qa.ai_no_data', 'warning');
      return;
    }

    const fromFlat = this.flattenObject(fromRecord.extraction_data || {});
    const toFlat = this.flattenObject(toRecord.extraction_data || {});

    const mismatches: { field: string; from: string; to: string }[] = [];
    const allKeys = new Set([...Object.keys(fromFlat), ...Object.keys(toFlat)]);

    allKeys.forEach(key => {
      if (key.startsWith('data.historical_data')) return;
      const fromVal = fromFlat[key];
      const toVal = toFlat[key];
      if (!this.valuesMatch(fromVal, toVal)) {
        mismatches.push({
          field: key,
          from: this.formatValue(fromVal),
          to: this.formatValue(toVal),
        });
      }
    });

    const fromLabel = this.getTypeLabel(this.selectedCompare.from);
    const toLabel = this.getTypeLabel(this.selectedCompare.to);

    let prompt = `I have an invoice data extraction comparison between ${fromLabel} and ${toLabel}.\n`;
    prompt += `Energy type: ${this.qaData?.stats?.energy_type || 'unknown'}\n`;
    prompt += `File: ${this.qaData?.stats?.file_name || 'unknown'}\n\n`;

    if (mismatches.length === 0) {
      prompt += `All fields match between ${fromLabel} and ${toLabel}. No discrepancies found.\n`;
    } else {
      prompt += `There are ${mismatches.length} mismatched fields out of ${allKeys.size} total:\n\n`;
      prompt += `| Field | ${fromLabel} | ${toLabel} |\n`;
      prompt += `|-------|--------|--------|\n`;
      mismatches.forEach(m => {
        prompt += `| ${m.field} | ${m.from} | ${m.to} |\n`;
      });
    }

    prompt += `\nPlease analyze these extraction differences and:\n`;
    prompt += `1. Identify which extraction version is more likely correct for each mismatch\n`;
    prompt += `2. Flag any values that look like formatting differences vs actual data errors\n`;
    prompt += `3. Suggest which fields need manual review\n`;
    prompt += `4. Provide a confidence score (high/medium/low) for each mismatch assessment\n`;

    this.generatedPrompt = prompt;
  }

  copyPromptToClipboard(): void {
    if (!this.generatedPrompt) return;
    navigator.clipboard.writeText(this.generatedPrompt).then(() => {
      this.toast.showToast('shared.success', 'admin.qa.ai_prompt_copied', 'success');
    });
  }

  copySummaryToClipboard(): void {
    if (!this.aiSummary) return;
    let text = this.aiSummary;
    if (this.aiHighlights.length) {
      text += '\n\nHighlights:\n' + this.aiHighlights.map(h => `- ${h}`).join('\n');
    }
    if (this.aiRecommendations.length) {
      text += '\n\nRecommendations:\n' + this.aiRecommendations.map(r => `- ${r}`).join('\n');
    }
    navigator.clipboard.writeText(text).then(() => {
      this.toast.showToast('shared.success', 'admin.qa.ai_summary_copied', 'success');
    });
  }

  exportComparisonCSV(): void {
    const rows = [['Field', 'V1', 'V2', 'Updated (Ground Truth)', 'V1 Match', 'V2 Match']];
    this.filteredComparisons.forEach(c => {
      rows.push([
        c.field,
        this.formatValue(c.erik),
        this.formatValue(c.neox),
        this.formatValue(c.updated),
        c.erikMatch ? 'Yes' : 'No',
        c.neoxMatch ? 'Yes' : 'No',
      ]);
    });

    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qa-comparison-${this.qaData?.stats?.file_name || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private getRecordByType(type: ExtractedByType): ExtractionQAGet | null {
    switch (type) {
      case ExtractedByType.ERIK: return this.erikRecord;
      case ExtractedByType.NEOX: return this.neoxRecord;
      case ExtractedByType.UPDATED: return this.updatedRecord;
      default: return null;
    }
  }

  private getTypeLabel(type: ExtractedByType): string {
    switch (type) {
      case ExtractedByType.ERIK: return 'V1';
      case ExtractedByType.NEOX: return 'V2';
      case ExtractedByType.UPDATED: return 'Updated (Ground Truth)';
      default: return type;
    }
  }

  private flattenObject(obj: any, prefix: string = ''): { [key: string]: any } {
    const result: { [key: string]: any } = {};

    for (const key of Object.keys(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(result, this.flattenObject(value, fullKey));
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item !== null && typeof item === 'object') {
            Object.assign(result, this.flattenObject(item, `${fullKey}[${index}]`));
          } else {
            result[`${fullKey}[${index}]`] = item;
          }
        });
      } else {
        result[fullKey] = value;
      }
    }

    return result;
  }

  private valuesMatch(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;

    const strA = String(a).trim().toLowerCase();
    const strB = String(b).trim().toLowerCase();
    return strA === strB;
  }

  formatValue(value: any): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }
}
